import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Star, ShieldCheck, CalendarBlank, Truck, Package as PackageIcon, ChatCircle, CreditCard, ArrowLeft, Heart, AppleLogo, GoogleLogo, ShoppingCart, Receipt } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={14} weight={i <= rating ? "fill" : "regular"} className={i <= rating ? "text-[#E8A341]" : "text-[#EAE7E0]"} />
      ))}
    </div>
  );
}

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, wishlistIds, toggleWishlist } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [bookedDates, setBookedDates] = useState([]);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("pickup");
  const [insurance, setInsurance] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedImg, setSelectedImg] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/items/${id}`).then(r => setItem(r.data)).catch(() => navigate("/browse")).finally(() => setLoading(false));
    api.get(`/bookings/item/${id}`).then(r => {
      const dates = [];
      (r.data || []).forEach(b => {
        let d = new Date(b.start_date);
        const end = new Date(b.end_date);
        while (d <= end) { dates.push(new Date(d)); d.setDate(d.getDate() + 1); }
      });
      setBookedDates(dates);
    }).catch(() => {});
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /></div>;
  if (!item) return null;

  const images = item.images?.length ? item.images.map(p => `${API_URL}/api/files/${p}`) : ["https://images.unsplash.com/photo-1650368237704-bf3d79a8b6b4?w=800&q=80"];
  const isOwner = user && user.id === item.owner_id;
  const days = dateRange.from && dateRange.to ? Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24)) : 0;
  const subtotal = days * (item.price_per_day || 0);
  const serviceFee = subtotal * 0.05;
  const taxRate = (() => { const p = (item.location || "").split(","); for (const s of p.reverse()) { const a = s.trim().toUpperCase(); if (["TX","CA","NY","FL","IL","PA","OH","GA","NC","MI","NJ","VA","WA","AZ","MA","TN","IN","MO","MD","WI","CO","MN","SC","AL","LA","KY","OR","OK","CT","UT","IA","NV","AR","MS","KS","NM","NE","HI","NH","WV","ID","ME","MT","RI","DE","SD","ND","AK","VT","WY","DC"].includes(a)) return a; } return ""; })();
  const taxRateNum = (() => { const rates = {"TX":0.0625,"CA":0.0725,"NY":0.04,"FL":0.06,"IL":0.0625,"PA":0.06,"OH":0.0575,"GA":0.04,"NC":0.0475,"MI":0.06,"NJ":0.06625,"VA":0.053,"WA":0.065,"AZ":0.056,"MA":0.0625,"TN":0.07,"IN":0.07,"MO":0.04225,"MD":0.06,"WI":0.05,"CO":0.029,"MN":0.06875,"SC":0.06,"AL":0.04,"LA":0.0445,"KY":0.06,"OR":0.0,"OK":0.045,"CT":0.0635,"UT":0.061,"IA":0.06,"NV":0.0685,"AR":0.065,"MS":0.07,"KS":0.065,"NM":0.05125,"NE":0.055,"HI":0.04,"NH":0.0,"WV":0.06,"ID":0.06,"ME":0.055,"MT":0.0,"RI":0.07,"DE":0.0,"SD":0.042,"ND":0.05,"AK":0.0,"VT":0.06,"WY":0.04,"DC":0.06}; return rates[taxRate] || 0; })();
  const tax = (subtotal + serviceFee) * taxRateNum;
  const insuranceFee = insurance && item.insurance_available ? subtotal * 0.1 : 0;
  const deposit = item.deposit_amount || 0;
  const total = subtotal + serviceFee + tax + insuranceFee + deposit;

  const handleBook = async () => {
    if (!user) { navigate("/login"); return; }
    if (!dateRange.from || !dateRange.to || days <= 0) { toast.error("Please select rental dates"); return; }
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        item_id: item.id,
        start_date: dateRange.from.toISOString().split("T")[0],
        end_date: dateRange.to.toISOString().split("T")[0],
        delivery_method: deliveryMethod,
        insurance_selected: insurance,
      });
      const { data: checkout } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      if (checkout.url) window.location.href = checkout.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
      setBookingDialog(false);
    }
  };

  const handleMessage = () => {
    if (!user) { navigate("/login"); return; }
    navigate(`/messages/${item.owner_id}`);
  };

  const handleAddToCart = async () => {
    if (!user) { navigate("/login"); return; }
    if (!dateRange.from || !dateRange.to || days <= 0) { toast.error("Please select rental dates"); return; }
    try {
      await api.post("/cart/add", {
        item_id: item.id,
        start_date: dateRange.from.toISOString().split("T")[0],
        end_date: dateRange.to.toISOString().split("T")[0],
        delivery_method: deliveryMethod,
        insurance_selected: insurance,
      });
      toast.success("Added to cart!");
      setBookingDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add"); }
  };

  const isWishlisted = wishlistIds?.has(item.id);

  const handleToggleWishlist = async () => {
    if (!user) { navigate("/login"); return; }
    const success = await toggleWishlist(item.id);
    if (success) {
      toast.success(isWishlisted ? "Removed from wishlist" : "Added to wishlist");
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#5C665D] hover:text-[#2C5545] mb-6 transition-colors" data-testid="item-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Images */}
          <div className="lg:col-span-3 space-y-3">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#F5F3EE]">
              <img src={images[selectedImg]} alt={item.title} className="w-full h-full object-cover animate-scale-in" data-testid="item-main-image" />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${i === selectedImg ? "border-[#2C5545]" : "border-transparent"}`} data-testid={`item-thumb-${i}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <p className="overline text-[#D96C4A] mb-2">{item.category?.replace(/_/g, " ")}</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-3" data-testid="item-title">{item.title}</h1>
              <div className="flex items-center gap-3 text-sm text-[#5C665D]">
                <span className="flex items-center gap-1"><MapPin size={14} weight="duotone" /> {item.location}</span>
                {item.avg_rating > 0 && <span className="flex items-center gap-1"><Star size={14} weight="fill" className="text-[#E8A341]" /> {item.avg_rating} ({item.reviews?.length})</span>}
              </div>
            </div>

            {/* Pricing */}
            <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-semibold text-[#2C5545]" data-testid="item-price">${item.price_per_day?.toFixed(2)}</span>
                <span className="text-sm text-[#5C665D]">/ day</span>
              </div>
              {(item.price_per_week || item.price_per_month) && (
                <div className="flex gap-4 text-sm text-[#5C665D] mb-4">
                  {item.price_per_week && <span>${item.price_per_week.toFixed(2)}/week</span>}
                  {item.price_per_month && <span>${item.price_per_month.toFixed(2)}/month</span>}
                </div>
              )}
              <div className="flex flex-wrap gap-2 text-xs">
                {item.deposit_amount > 0 && <span className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-3 py-1"><ShieldCheck size={12} weight="duotone" className="inline mr-1" />Deposit: ${item.deposit_amount.toFixed(2)}</span>}
                {item.insurance_available && <span className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-3 py-1">Insurance Available</span>}
                {item.delivery_available && <span className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-3 py-1"><Truck size={12} weight="duotone" className="inline mr-1" />Delivery</span>}
                {item.pickup_available && <span className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-3 py-1"><PackageIcon size={12} weight="duotone" className="inline mr-1" />Pickup</span>}
              </div>
            </div>

            {/* Actions */}
            {!isOwner && (
              <div className="space-y-3">
                <button onClick={() => setBookingDialog(true)} className="w-full bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3.5 font-medium transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 flex items-center justify-center gap-2" data-testid="item-book-btn">
                  <CalendarBlank size={18} weight="duotone" /> Book Now
                </button>
                <div className="flex gap-3">
                  <button onClick={handleMessage} className="flex-1 bg-white text-[#1A1C1A] border border-[#EAE7E0] hover:border-[#2C5545] rounded-full py-3 font-medium transition-all flex items-center justify-center gap-2 text-sm" data-testid="item-message-btn">
                    <ChatCircle size={16} weight="duotone" /> Message
                  </button>
                  <button onClick={handleToggleWishlist} className={`px-4 border rounded-full py-3 transition-all flex items-center justify-center gap-2 text-sm font-medium ${isWishlisted ? "bg-[#D96C4A]/10 border-[#D96C4A]/30 text-[#D96C4A]" : "bg-white border-[#EAE7E0] text-[#5C665D] hover:border-[#D96C4A]"}`} data-testid="item-wishlist-btn">
                    <Heart size={16} weight={isWishlisted ? "fill" : "regular"} /> {isWishlisted ? "Saved" : "Save"}
                  </button>
                </div>

                {/* Payment methods info */}
                <div className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl p-3">
                  <p className="text-xs font-semibold text-[#5C665D] uppercase tracking-wider mb-2">Accepted Payments</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#1A1C1A]"><CreditCard size={12} weight="duotone" /> Card</span>
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#1A1C1A]"><AppleLogo size={12} weight="fill" /> Apple Pay</span>
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#1A1C1A]"><GoogleLogo size={12} weight="fill" /> Google Pay</span>
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#5C665D]">PayPal</span>
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#5C665D]">Venmo</span>
                    <span className="flex items-center gap-1 bg-white border border-[#EAE7E0] rounded-lg px-2.5 py-1 text-xs text-[#5C665D]">Cash App</span>
                  </div>
                </div>
              </div>
            )}

            {/* Owner */}
            {item.owner && (
              <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                <p className="overline text-[#5C665D] mb-3">Listed by</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#2C5545]/10 flex items-center justify-center text-[#2C5545] font-medium">{item.owner.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <p className="text-sm font-medium text-[#1A1C1A]">{item.owner.name}</p>
                    <p className="text-xs text-[#5C665D]">{item.owner.location || "Local"}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-xl font-medium text-[#1A1C1A] mb-4">About this item</h2>
            <p className="text-sm text-[#5C665D] leading-relaxed whitespace-pre-line" data-testid="item-description">{item.description}</p>
            <div className="mt-4 flex gap-3 text-sm">
              <span className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-4 py-1.5 text-[#5C665D]">Condition: {item.condition}</span>
            </div>
          </div>
        </div>

        {/* Reviews */}
        {item.reviews?.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading text-xl font-medium text-[#1A1C1A] mb-6">Reviews ({item.reviews.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {item.reviews.map(r => (
                <div key={r.id} className="bg-white border border-[#EAE7E0] rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#1A1C1A]">{r.reviewer_name}</span>
                    <StarRating rating={r.rating} />
                  </div>
                  <p className="text-sm text-[#5C665D]">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onOpenChange={setBookingDialog}>
        <DialogContent className="bg-white border-[#EAE7E0] rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#1A1C1A]">Book {item.title}</DialogTitle>
            <DialogDescription className="text-sm text-[#5C665D]">Select your rental dates and options.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div>
              <p className="text-sm font-medium text-[#1A1C1A] mb-2">Select Dates</p>
              <div className="flex justify-center">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  disabled={[{ before: new Date() }, ...bookedDates.map(d => new Date(d))]}
                  numberOfMonths={1}
                  className="rounded-xl border border-[#EAE7E0]"
                  data-testid="booking-calendar"
                />
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-[#1A1C1A] mb-2">Delivery Method</p>
              <div className="flex gap-3">
                {item.pickup_available && (
                  <button onClick={() => setDeliveryMethod("pickup")} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${deliveryMethod === "pickup" ? "bg-[#2C5545] text-white border-[#2C5545]" : "bg-white text-[#5C665D] border-[#EAE7E0]"}`} data-testid="booking-pickup-btn">Pickup</button>
                )}
                {item.delivery_available && (
                  <button onClick={() => setDeliveryMethod("delivery")} className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-all ${deliveryMethod === "delivery" ? "bg-[#2C5545] text-white border-[#2C5545]" : "bg-white text-[#5C665D] border-[#EAE7E0]"}`} data-testid="booking-delivery-btn">Delivery</button>
                )}
              </div>
            </div>

            {item.insurance_available && (
              <label className="flex items-center gap-3 cursor-pointer" data-testid="booking-insurance-toggle">
                <input type="checkbox" checked={insurance} onChange={e => setInsurance(e.target.checked)} className="w-4 h-4 rounded border-[#EAE7E0] text-[#2C5545] focus:ring-[#2C5545]" />
                <span className="text-sm text-[#1A1C1A]">Add rental insurance (+10%)</span>
              </label>
            )}

            {days > 0 && (
              <div className="bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl p-4 space-y-2 text-sm" data-testid="booking-price-breakdown">
                <div className="flex justify-between text-[#5C665D]"><span>${item.price_per_day.toFixed(2)} x {days} days</span><span>${subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-[#5C665D]"><span>Service fee (5%)</span><span>${serviceFee.toFixed(2)}</span></div>
                {taxRateNum > 0 && <div className="flex justify-between text-[#5C665D]"><span>Tax ({(taxRateNum * 100).toFixed(2)}%{taxRate ? ` ${taxRate}` : ""})</span><span>${tax.toFixed(2)}</span></div>}
                {insuranceFee > 0 && <div className="flex justify-between text-[#5C665D]"><span>Insurance (10%)</span><span>${insuranceFee.toFixed(2)}</span></div>}
                {deposit > 0 && <div className="flex justify-between text-[#5C665D]"><span>Security deposit</span><span>${deposit.toFixed(2)}</span></div>}
                <div className="flex justify-between font-semibold text-[#1A1C1A] pt-2 border-t border-[#EAE7E0]"><span>Total</span><span>${total.toFixed(2)}</span></div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleBook} disabled={submitting || days <= 0} className="flex-1 bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3.5 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="booking-confirm-btn">
                {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CreditCard size={18} weight="duotone" /> Pay Now</>}
              </button>
              <button onClick={handleAddToCart} disabled={days <= 0} className="bg-white text-[#2C5545] border border-[#2C5545] hover:bg-[#2C5545]/5 rounded-full px-5 py-3.5 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2" data-testid="booking-add-cart-btn">
                <ShoppingCart size={18} weight="duotone" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
